import { useState, useEffect, Fragment, useRef } from "react";
import {
    Dialog, DialogTitle, DialogContent, TextField, DialogActions,
    Box, Chip, IconButton, alpha, Snackbar, Alert, useTheme, Container, Typography,
    Button, Collapse, TableCell, TableRow,
} from "@mui/material";
import { SkeletonProjectCard } from "../components/ux/Skeleton";
import { useNavigate } from "react-router-dom";
import {
    useAuth,
    useThemeMode,
    vortxColors,
    ProjectTableContainer,
    ProjectTableHead,
    ProjectTableHeaderCell,
    ProjectTableBody,
    AddExistingProjectDialog,
    UnifiedScenarioTable,
    SharedPagesDashboard,
    ShareConfigDialog,
} from '@vortx/shared-ui';
import { API_BASE_URL, useUnifiedProjects, vortxTypography, retroColors } from '@vortx/shared-ui';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import TerminalIcon from '@mui/icons-material/Terminal';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import ScenarioCompareModalV2 from '../components/ScenarioCompareModalV2';

export default function Projects() {
    // Use unified projects hook - filter by site_dna app
    const {
        projects: allProjects,
        loading: projectsLoading,
        error: projectsError,
        createProject: createUnifiedProject,
        updateProject: updateUnifiedProject,
        deleteProject: deleteUnifiedProject,
        loadProjects: reloadProjects,
    } = useUnifiedProjects('site_dna');

    // Filter to only show Site DNA enabled projects
    const projects = allProjects.filter(p => p.apps?.site_dna?.enabled);
    const loading = projectsLoading;

    const [addExistingOpen, setAddExistingOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [newProjectName, setNewProjectName] = useState("");

    // Expanded projects state - tracks scenario data (preloaded) and which projects are visually expanded
    const [expandedProjects, setExpandedProjects] = useState({}); // stores scenario data per project
    const [visibleExpanded, setVisibleExpanded] = useState({}); // tracks which projects are visually expanded
    const loadedProjectIdsRef = useRef(new Set()); // tracks which projects have scenarios loaded (using ref to avoid re-renders)
    const initialLoadDone = useRef(false); // tracks if initial load has been done
    const [loadingScenarios, setLoadingScenarios] = useState({});

    // Project dialogs
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [projectToRename, setProjectToRename] = useState(null);
    const [tempProjectName, setTempProjectName] = useState("");

    // Scenario dialogs
    const [scenarioRenameDialogOpen, setScenarioRenameDialogOpen] = useState(false);
    const [scenarioDeleteDialogOpen, setScenarioDeleteDialogOpen] = useState(false);
    const [scenarioDuplicateDialogOpen, setScenarioDuplicateDialogOpen] = useState(false);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [tempScenarioName, setTempScenarioName] = useState("");
    const [duplicateScenarioName, setDuplicateScenarioName] = useState("");

    // Compare scenarios state
    const [selectedForCompare, setSelectedForCompare] = useState({}); // { projectId: Set of scenario ids }
    const [compareModalOpen, setCompareModalOpen] = useState(false);
    const [compareProjectId, setCompareProjectId] = useState(null);
    const [compareScenarios, setCompareScenarios] = useState([]); // Full scenario data for comparison
    const [loadingCompare, setLoadingCompare] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [sharedPagesOpen, setSharedPagesOpen] = useState(false);

    // Single-scenario share state
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareScenario, setShareScenario] = useState(null);
    const [shareProjectId, setShareProjectId] = useState(null);
    const [shareProjectName, setShareProjectName] = useState('');
    const [shareScenarioData, setShareScenarioData] = useState(null);

    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { isDark, isRetro } = useThemeMode();
    const theme = useTheme();

    // Theme-aware colors
    const bgMain = isRetro ? 'transparent' : theme.palette.background.default;
    const bgCard = isRetro ? 'rgba(0, 0, 0, 0.4)' : theme.palette.background.paper;
    const textPrimary = isRetro ? '#f4f1ec' : theme.palette.text.primary;
    const textSecondary = isRetro ? '#888' : theme.palette.text.secondary;

    const getHeaders = async () => {
        const headers = { 'Content-Type': 'application/json' };
        if (currentUser) {
            const token = await currentUser.getIdToken();
            headers['Authorization'] = `Bearer ${token}`;
        } else {
            headers['X-Dev-User-Id'] = 'tim@vortx.us';
        }
        return headers;
    };

    // Preload scenarios for all projects on mount (mimics old behavior)
    // Only loads scenarios for projects that haven't been loaded yet
    useEffect(() => {
        if (!loading && projects.length > 0) {
            // Check if there are any new projects that need scenario loading
            const newProjects = projects.filter(p => !loadedProjectIdsRef.current.has(p.id));

            if (newProjects.length > 0) {
                newProjects.forEach(project => {
                    loadScenariosForProject(project.id);
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, projects.length]); // Only depend on loading and projects.length, not projects array reference

    const loadScenariosForProject = async (projectId) => {
        setLoadingScenarios(prev => ({ ...prev, [projectId]: true }));
        try {
            const headers = await getHeaders();
            // Use cache: 'no-store' to ensure fresh data
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios`, { headers, cache: 'no-store' });
            if (res.ok) {
                const data = await res.json();
                setExpandedProjects(prev => ({
                    ...prev,
                    [projectId]: data.scenarios || []
                }));
                // Mark project as loaded to prevent re-fetching (using ref to avoid triggering re-renders)
                loadedProjectIdsRef.current.add(projectId);
            }
        } catch (error) {
            console.error("Failed to load scenarios", error);
            setSnackbar({ open: true, message: "Failed to load scenarios", severity: "error" });
        } finally {
            setLoadingScenarios(prev => ({ ...prev, [projectId]: false }));
        }
    };

    const toggleExpand = (projectId) => {
        // Scenarios are preloaded, just toggle visibility instantly
        setVisibleExpanded(prev => ({
            ...prev,
            [projectId]: !prev[projectId]
        }));
    };

    const isExpanded = (projectId) => {
        return !!visibleExpanded[projectId];
    };

    // Project CRUD operations
    const handleCreateProject = async () => {
        // Validate project name
        if (!newProjectName?.trim()) {
            setSnackbar({ open: true, message: "Project name cannot be empty", severity: "error" });
            return;
        }

        try {
            // Create project with Site DNA app enabled
            await createUnifiedProject({
                name: newProjectName.trim(),
                apps: {
                    site_dna: {
                        enabled: true,
                        config: {}
                    }
                }
            });

            setOpen(false);
            setNewProjectName("");
            setSnackbar({ open: true, message: "Project created successfully", severity: "success" });
        } catch (error) {
            console.error("Failed to create project:", error);
            setSnackbar({ open: true, message: `Failed to create project: ${error.message}`, severity: "error" });
        }
    };

    const openDeleteDialog = (project, e) => {
        e.stopPropagation();
        setProjectToDelete(project);
        setDeleteDialogOpen(true);
    };

    const handleDeleteProject = async () => {
        if (!projectToDelete) return;

        const deletedProjectId = projectToDelete.id;
        setDeleteDialogOpen(false);
        setProjectToDelete(null);
        setSnackbar({ open: true, message: "Deleting project...", severity: "info" });

        try {
            // Hook handles optimistic updates automatically
            await deleteUnifiedProject(deletedProjectId);
            setSnackbar({ open: true, message: "Project deleted successfully", severity: "success" });
        } catch (error) {
            console.error("Failed to delete project:", error);
            setSnackbar({ open: true, message: `Failed to delete project: ${error.message}`, severity: "error" });
        }
    };

    const openRenameDialog = (project, e) => {
        e.stopPropagation();
        setProjectToRename(project);
        setTempProjectName(project.name);
        setRenameDialogOpen(true);
    };

    const handleRenameProject = async () => {
        if (!projectToRename) return;
        try {
            // Hook handles optimistic updates automatically
            await updateUnifiedProject(projectToRename.id, { name: tempProjectName });
            setRenameDialogOpen(false);
            setProjectToRename(null);
            setSnackbar({ open: true, message: "Project renamed successfully", severity: "success" });
        } catch (error) {
            console.error("Failed to rename project:", error);
            setSnackbar({ open: true, message: `Failed to rename project: ${error.message}`, severity: "error" });
        }
    };

    // Scenario CRUD operations
    const openScenarioRenameDialog = (projectId, scenario, e) => {
        e.stopPropagation();
        setSelectedProjectId(projectId);
        setSelectedScenario(scenario);
        setTempScenarioName(scenario.name);
        setScenarioRenameDialogOpen(true);
    };

    const handleRenameScenario = async () => {
        if (!selectedScenario || !selectedProjectId) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${selectedProjectId}/scenarios/${selectedScenario.id}`, {
                method: "PUT",
                headers,
                body: JSON.stringify({ name: tempScenarioName })
            });

            if (res.ok) {
                setScenarioRenameDialogOpen(false);
                setSnackbar({ open: true, message: "Scenario renamed", severity: "success" });
                loadScenariosForProject(selectedProjectId);
            } else {
                const text = await res.text();
                setSnackbar({ open: true, message: `Failed to rename: ${text}`, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to rename scenario", severity: "error" });
        }
    };

    const openScenarioDeleteDialog = (projectId, scenario, e) => {
        e.stopPropagation();
        setSelectedProjectId(projectId);
        setSelectedScenario(scenario);
        setScenarioDeleteDialogOpen(true);
    };

    const handleDeleteScenario = async () => {
        if (!selectedScenario || !selectedProjectId) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${selectedProjectId}/scenarios/${selectedScenario.id}`, {
                method: "DELETE",
                headers
            });

            if (res.ok) {
                setScenarioDeleteDialogOpen(false);
                setSnackbar({ open: true, message: "Scenario deleted", severity: "success" });
                loadScenariosForProject(selectedProjectId);
            } else {
                setSnackbar({ open: true, message: "Failed to delete scenario", severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to delete scenario", severity: "error" });
        }
    };

    const openScenarioDuplicateDialog = (projectId, scenario, e) => {
        e.stopPropagation();
        setSelectedProjectId(projectId);
        setSelectedScenario(scenario);
        setDuplicateScenarioName(`${scenario.name} (Copy)`);
        setScenarioDuplicateDialogOpen(true);
    };

    const handleDuplicateScenario = async () => {
        if (!selectedScenario || !selectedProjectId) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${selectedProjectId}/scenarios/${selectedScenario.id}/duplicate`, {
                method: "POST",
                headers,
                body: JSON.stringify({ name: duplicateScenarioName })
            });

            if (res.ok) {
                setScenarioDuplicateDialogOpen(false);
                setSnackbar({ open: true, message: "Scenario duplicated", severity: "success" });
                loadScenariosForProject(selectedProjectId);
            } else {
                const errorData = await res.json();
                setSnackbar({ open: true, message: `Failed to duplicate: ${errorData.error}`, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to duplicate scenario", severity: "error" });
        }
    };

    const handleNewScenario = (projectId) => {
        navigate(`/projects/${projectId}/scenarios/new`);
    };

    // Single-scenario share
    const openScenarioShareDialog = async (projectId, scenario, e) => {
        e.stopPropagation();
        const project = projects.find(p => p.id === projectId);
        setShareProjectId(projectId);
        setShareProjectName(project?.name || 'Project');
        setShareScenario(scenario);
        setShareDialogOpen(true);

        try {
            const headers = await getHeaders();
            const res = await fetch(
                `${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${scenario.id}`,
                { headers, cache: 'no-store' }
            );
            if (res.ok) {
                const fullScenario = await res.json();
                setShareScenarioData(fullScenario);
            }
        } catch (error) {
            console.error('Error fetching scenario for share:', error);
        }
    };

    // Helper to format system config
    const formatSystemConfig = (scenario) => {
        const config = scenario.system_config_summary || {};
        const parts = [];

        if (config.pv_kw && config.pv_kw > 0) {
            const pvLabel = config.pv_existing ? 'PV (E)' : 'PV';
            parts.push(`${pvLabel}: ${Number(config.pv_kw).toFixed(0)} kW`);
        }

        if (config.storage_kw > 0 || config.storage_kwh > 0) {
            parts.push(`Storage: ${Number(config.storage_kw || 0).toFixed(0)} kW / ${Number(config.storage_kwh || 0).toFixed(0)} kWh`);
        }

        if (config.generator_kw && config.generator_kw > 0) {
            parts.push(`Gen: ${Number(config.generator_kw).toFixed(0)} kW`);
        }

        return parts.length > 0 ? parts.join(' • ') : '—';
    };

    // Helper to get tariff name
    const getTariffName = (scenario) => {
        // Check lightweight tariff_display from Firestore metadata first
        const display = scenario.tariff_display;
        if (display?.name) return display.name;

        const tariff = scenario.tariff_data;

        // Primary: Check tariff_data for human-readable name fields
        if (tariff) {
            // Try various name fields that might be set
            if (tariff.label) return tariff.label;
            if (tariff.name) return tariff.name;
            if (tariff.rateName) return tariff.rateName;
            // Combine utility and rate schedule if available
            if (tariff.rate_schedule_name) {
                return tariff.utility_name
                    ? `${tariff.utility_name} ${tariff.rate_schedule_name}`
                    : tariff.rate_schedule_name;
            }
            // Check solver format naming
            if (tariff.urdb_rate_name) {
                return tariff.urdb_utility_name
                    ? `${tariff.urdb_utility_name} ${tariff.urdb_rate_name}`
                    : tariff.urdb_rate_name;
            }
            // Check nested rate_data for name
            if (tariff.rate_data?.name) return tariff.rate_data.name;
            if (tariff.rate_data?.rateName) return tariff.rate_data.rateName;
            // Check solver response format
            if (tariff.urdb_response?.name) return tariff.urdb_response.name;
            if (tariff.urdb_response?.rateName) return tariff.urdb_response.rateName;
        }

        // Secondary: check if tariff_label looks like a readable name (not a database ID)
        // Database IDs are typically 20+ character alphanumeric strings
        if (scenario.tariff_label && !/^[A-Za-z0-9]{15,}$/.test(scenario.tariff_label)) {
            return scenario.tariff_label;
        }

        // Check reopt_inputs for legacy scenarios (may have tariff name even with database ID)
        const inputs = scenario.reopt_inputs || {};
        const tariffName = inputs.tariff_name || inputs.electricity_tariff_name || scenario.tariff_name;
        if (tariffName) {
            return tariffName;
        }

        // If tariff_label exists but no readable name found, show placeholder
        // (scenario was saved before full tariff data was being stored)
        if (scenario.tariff_label) {
            return 'Tariff set';
        }

        return '—';
    };

    // Compare scenarios handlers
    const toggleScenarioForCompare = (projectId, scenarioId, e) => {
        e.stopPropagation();
        setSelectedForCompare(prev => {
            const projectSelections = new Set(prev[projectId] || []);
            if (projectSelections.has(scenarioId)) {
                projectSelections.delete(scenarioId);
            } else {
                projectSelections.add(scenarioId);
            }
            return { ...prev, [projectId]: projectSelections };
        });
    };

    const getSelectedCount = (projectId) => {
        return selectedForCompare[projectId]?.size || 0;
    };

    const openCompareModal = async (projectId) => {
        const selectedIds = selectedForCompare[projectId] || new Set();
        if (selectedIds.size < 2) return;

        setCompareProjectId(projectId);
        setLoadingCompare(true);
        setCompareModalOpen(true);

        try {
            const headers = await getHeaders();
            // Fetch full scenario data for each selected scenario
            // Use cache: 'no-store' to ensure we get fresh data after saves
            const scenarioPromises = Array.from(selectedIds).map(scenarioId =>
                fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${scenarioId}`, { headers, cache: 'no-store' })
                    .then(res => res.ok ? res.json() : null)
            );

            const fullScenarios = await Promise.all(scenarioPromises);
            const validScenarios = fullScenarios.filter(s => s !== null);

            // Debug logging for scenario comparison data
            validScenarios.forEach(s => {
                const fs = s.ui_state?.financialSummary;
                if (process.env.NODE_ENV === 'development') {
                    console.log('[CompareDebug] Scenario:', s.name, {
                        id: s.id,
                        hasFinancialSummary: !!fs,
                        npv: fs?.npv,
                        paybackPeriod: fs?.paybackPeriod,
                        annualSavings: fs?.annualSavings,
                        year1NetSavings: fs?.year1NetSavings,
                        capex_total: fs?.capex_breakdown?.total,
                        capex_net: fs?.capex_breakdown?.net_capex,
                        initialInvestment: fs?.initialInvestment,
                        hasCashFlows: !!fs?.cash_flows?.length,
                        // Form data (for system config)
                        hasForm: !!s.ui_state?.form,
                        formSolar: s.ui_state?.form?.solar,
                        formBattery: s.ui_state?.form?.battery,
                        formGenerator: s.ui_state?.form?.generator,
                    });
                }
            });

            setCompareScenarios(validScenarios);
        } catch (error) {
            console.error('Error fetching scenarios for comparison:', error);
            setSnackbar({ open: true, message: 'Failed to load scenario details', severity: 'error' });
        } finally {
            setLoadingCompare(false);
        }
    };

    const clearCompareSelection = (projectId) => {
        setSelectedForCompare(prev => ({ ...prev, [projectId]: new Set() }));
    };

    return (
        <Box sx={{
            minHeight: isRetro ? 'auto' : '100vh',
            background: bgMain,
            py: isRetro ? 0 : 6
        }}>
            <Container maxWidth="xl" sx={{ px: isRetro ? 0 : undefined }}>
                {/* Header Section */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: isRetro ? 4 : 5
                }}>
                    <Box>
                        {isRetro ? (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Box sx={{
                                        width: 4,
                                        height: 32,
                                        background: vortxColors.gold,
                                        boxShadow: `0 0 10px ${vortxColors.gold}`,
                                    }} />
                                    <Typography sx={{
                                        fontFamily: vortxTypography.heading,
                                        fontSize: '1.5rem',
                                        fontWeight: 700,
                                        color: vortxColors.gold,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.15em',
                                        textShadow: `0 0 20px rgba(178, 140, 80, 0.4)`,
                                    }}>
                                        Project Archive
                                    </Typography>
                                </Box>
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    color: '#888',
                                    pl: 3,
                                    fontStyle: 'italic',
                                }}>
                                    expand a project to view scenarios
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography variant="h3" sx={{
                                    fontFamily: vortxTypography.heading,
                                    color: vortxColors.gold,
                                    fontWeight: 700,
                                    mb: 1,
                                    letterSpacing: '0.02em'
                                }}>
                                    YOUR PROJECTS
                                </Typography>
                                <Typography variant="body1" sx={{
                                    fontFamily: vortxTypography.body,
                                    color: 'text.secondary',
                                    fontSize: '1.1rem'
                                }}>
                                    Manage your energy optimization projects and scenarios
                                </Typography>
                            </>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ShareIcon />}
                            onClick={() => setSharedPagesOpen(true)}
                            sx={isRetro ? {
                                color: '#5fd4c4',
                                px: 3,
                                py: 1,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                fontFamily: vortxTypography.mono,
                                borderRadius: 0,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                border: '1px solid rgba(95, 212, 196, 0.5)',
                                '&:hover': {
                                    bgcolor: 'rgba(95, 212, 196, 0.1)',
                                    borderColor: '#5fd4c4',
                                }
                            } : {
                                color: vortxColors.gold,
                                px: 3,
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                fontFamily: vortxTypography.body,
                                borderRadius: 2,
                                textTransform: 'none',
                                borderColor: vortxColors.gold,
                                '&:hover': {
                                    borderColor: '#9e7940',
                                    bgcolor: alpha(vortxColors.gold, 0.08),
                                }
                            }}
                        >
                            Shared Pages
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<FolderOpenIcon />}
                            onClick={() => setAddExistingOpen(true)}
                            sx={isRetro ? {
                                color: '#5fd4c4',
                                borderColor: 'rgba(95, 212, 196, 0.5)',
                                fontFamily: vortxTypography.mono,
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                borderRadius: 0,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                '&:hover': {
                                    borderColor: '#5fd4c4',
                                    bgcolor: 'rgba(95, 212, 196, 0.1)',
                                    boxShadow: '0 0 15px rgba(95, 212, 196, 0.3)',
                                }
                            } : {
                                color: vortxColors.gold,
                                borderColor: vortxColors.gold,
                                fontFamily: vortxTypography.body,
                                fontWeight: 600,
                                borderRadius: 2,
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#9e7940',
                                    bgcolor: alpha(vortxColors.gold, 0.08),
                                }
                            }}
                        >
                            Add Existing
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={isRetro ? <TerminalIcon /> : <AddIcon />}
                            onClick={() => setOpen(true)}
                            sx={isRetro ? {
                            bgcolor: 'transparent',
                            color: '#5fd4c4',
                            px: 3,
                            py: 1,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            fontFamily: vortxTypography.mono,
                            borderRadius: 0,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            border: '1px solid rgba(95, 212, 196, 0.5)',
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: 'rgba(95, 212, 196, 0.1)',
                                borderColor: '#5fd4c4',
                                boxShadow: '0 0 15px rgba(95, 212, 196, 0.3)',
                            }
                        } : {
                            bgcolor: vortxColors.gold,
                            color: '#FFFFFF',
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            fontFamily: vortxTypography.body,
                            borderRadius: 2,
                            textTransform: 'none',
                            boxShadow: `0 2px 8px ${alpha(vortxColors.gold, 0.3)}`,
                            '&:hover': {
                                bgcolor: '#9e7940',
                                boxShadow: `0 4px 12px ${alpha(vortxColors.gold, 0.4)}`,
                            }
                        }}
                    >
                        New Project
                    </Button>
                    </Box>
                </Box>

                {/* Projects Table */}
                {projectsError ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        Failed to load projects: {projectsError}
                    </Alert>
                ) : loading ? (
                    <Box
                        className={`vx-stagger ${isRetro ? 'retro-mode' : ''}`}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                        {[1, 2, 3].map((i) => (
                            <Box
                                key={i}
                                className="vx-skeleton vx-slide-in-up"
                                sx={{
                                    height: 60,
                                    borderRadius: isRetro ? 0 : 2
                                }}
                            />
                        ))}
                    </Box>
                ) : projects.length === 0 ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        py: isRetro ? 8 : 12,
                        px: 3
                    }}>
                        <Box sx={{
                            bgcolor: isRetro ? 'transparent' : alpha(vortxColors.gold, 0.1),
                            border: isRetro ? '1px solid rgba(178, 140, 80, 0.3)' : 'none',
                            borderRadius: isRetro ? 0 : '50%',
                            p: 3,
                            mb: 3,
                        }}>
                            <FolderIcon sx={{ fontSize: isRetro ? 48 : 64, color: vortxColors.gold, opacity: isRetro ? 0.6 : 0.8 }} />
                        </Box>
                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontSize: isRetro ? '1.2rem' : '1.5rem',
                            fontWeight: isRetro ? 700 : 600,
                            color: isRetro ? vortxColors.gold : textPrimary,
                            textTransform: isRetro ? 'uppercase' : 'none',
                            letterSpacing: isRetro ? '0.1em' : 'normal',
                            mb: 1,
                        }}>
                            {isRetro ? 'Archive Empty' : 'No Projects Yet'}
                        </Typography>
                        <Typography sx={{
                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                            fontSize: isRetro ? '0.75rem' : '1rem',
                            color: isRetro ? '#888' : 'text.secondary',
                            mb: 4,
                            maxWidth: 400
                        }}>
                            {isRetro
                                ? 'No project records found. Initialize a new project to begin energy analysis operations.'
                                : 'Projects help you organize your energy optimization scenarios. Create your first one to get started.'}
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={isRetro ? <TerminalIcon /> : <AddIcon />}
                            onClick={() => setOpen(true)}
                            sx={isRetro ? {
                                bgcolor: 'transparent',
                                color: vortxColors.gold,
                                px: 4,
                                py: 1.5,
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                fontFamily: vortxTypography.mono,
                                borderRadius: 0,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                border: `1px solid ${vortxColors.gold}`,
                                boxShadow: `0 0 15px rgba(178, 140, 80, 0.2)`,
                                '&:hover': {
                                    bgcolor: 'rgba(178, 140, 80, 0.1)',
                                    boxShadow: `0 0 25px rgba(178, 140, 80, 0.4)`,
                                }
                            } : {
                                bgcolor: vortxColors.gold,
                                color: '#FFFFFF',
                                px: 5,
                                py: 2,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                fontFamily: vortxTypography.body,
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: `0 4px 12px ${alpha(vortxColors.gold, 0.4)}`,
                                '&:hover': {
                                    bgcolor: '#9e7940',
                                }
                            }}
                        >
                            {isRetro ? 'Initialize Project' : 'Create Your First Project'}
                        </Button>
                    </Box>
                ) : (
                    <ProjectTableContainer>
                        <ProjectTableHead>
                            <ProjectTableHeaderCell sx={{ width: 50 }} />
                            <ProjectTableHeaderCell>Project</ProjectTableHeaderCell>
                            <ProjectTableHeaderCell>Scenarios</ProjectTableHeaderCell>
                            <ProjectTableHeaderCell align="right">Actions</ProjectTableHeaderCell>
                        </ProjectTableHead>
                        <ProjectTableBody>
                                {projects.map((project, index) => (
                                    <Fragment key={project.id}>
                                        {/* Project Row */}
                                        <TableRow
                                            className="vx-slide-in-up vx-state-transition"
                                            onClick={() => toggleExpand(project.id)}
                                            sx={isRetro ? {
                                                cursor: 'pointer',
                                                bgcolor: isExpanded(project.id) ? 'rgba(178, 140, 80, 0.1)' : 'rgba(0, 0, 0, 0.4)',
                                                borderLeft: isExpanded(project.id) ? `3px solid ${vortxColors.gold}` : '3px solid transparent',
                                                '&:hover': {
                                                    bgcolor: 'rgba(178, 140, 80, 0.15)',
                                                },
                                                '& td': {
                                                    borderBottom: '1px solid rgba(63, 106, 100, 0.3)',
                                                }
                                            } : {
                                                cursor: 'pointer',
                                                bgcolor: isExpanded(project.id) ? alpha(vortxColors.gold, 0.08) : 'transparent',
                                                '&:hover': {
                                                    bgcolor: alpha(vortxColors.gold, 0.1),
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ width: 50 }}>
                                                <IconButton size="small" sx={{ color: isRetro ? '#5fd4c4' : vortxColors.gold }}>
                                                    {isExpanded(project.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <FolderIcon sx={{ color: vortxColors.gold, fontSize: isRetro ? 20 : 24 }} />
                                                    <Typography sx={isRetro ? {
                                                        fontFamily: vortxTypography.heading,
                                                        fontSize: '1rem',
                                                        fontWeight: 600,
                                                        color: '#f4f1ec',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em',
                                                    } : {
                                                        fontFamily: vortxTypography.heading,
                                                        fontSize: '1.1rem',
                                                        fontWeight: 600,
                                                        color: textPrimary,
                                                    }}>
                                                        {project.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${expandedProjects[project.id]?.length || 0} scenario${(expandedProjects[project.id]?.length || 0) !== 1 ? 's' : ''}`}
                                                    size="small"
                                                    sx={isRetro ? {
                                                        bgcolor: 'transparent',
                                                        color: '#5fd4c4',
                                                        border: '1px solid rgba(95, 212, 196, 0.3)',
                                                        fontFamily: vortxTypography.mono,
                                                        fontSize: '0.7rem',
                                                        borderRadius: 0,
                                                    } : {
                                                        bgcolor: alpha(vortxColors.teal, 0.15),
                                                        color: isDark ? vortxColors.white : vortxColors.charcoal,
                                                        fontFamily: vortxTypography.body,
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => openRenameDialog(project, e)}
                                                        title="Rename Project"
                                                        sx={isRetro ? {
                                                            color: retroColors.muted,
                                                            borderRadius: 0,
                                                            '&:hover': { color: vortxColors.gold, bgcolor: 'rgba(178, 140, 80, 0.1)' }
                                                        } : {
                                                            color: vortxColors.gray,
                                                            '&:hover': { color: vortxColors.gold, bgcolor: alpha(vortxColors.gold, 0.1) }
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => openDeleteDialog(project, e)}
                                                        title="Delete Project"
                                                        sx={isRetro ? {
                                                            color: retroColors.muted,
                                                            borderRadius: 0,
                                                            '&:hover': { color: '#e74c3c', bgcolor: 'rgba(231, 76, 60, 0.1)' }
                                                        } : {
                                                            color: vortxColors.gray,
                                                            '&:hover': { color: '#ff6b6b', bgcolor: alpha('#ff6b6b', 0.1) }
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>

                                        {/* Expanded Scenarios Section */}
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ p: 0, border: 'none' }}>
                                                <Collapse in={isExpanded(project.id)} timeout="auto" unmountOnExit>
                                                    <Box sx={isRetro ? {
                                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                                        borderLeft: `3px solid ${vortxColors.gold}`,
                                                        p: 2,
                                                        ml: 0,
                                                    } : {
                                                        bgcolor: alpha(vortxColors.warmStone, 0.3),
                                                        p: 2,
                                                        borderLeft: `4px solid ${vortxColors.gold}`,
                                                    }}>
                                                        {loadingScenarios[project.id] ? (
                                                            <Box
                                                                className={`vx-stagger ${isRetro ? 'retro-mode' : ''}`}
                                                                sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                                                            >
                                                                {[1, 2].map((i) => (
                                                                    <Box
                                                                        key={i}
                                                                        className="vx-skeleton vx-slide-in-up"
                                                                        sx={{ height: 40, borderRadius: isRetro ? 0 : 1 }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        ) : (expandedProjects[project.id]?.length || 0) === 0 ? (
                                                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                                                <Typography sx={{
                                                                    color: textSecondary,
                                                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                                                    fontSize: isRetro ? '0.8rem' : '0.9rem',
                                                                    mb: 2,
                                                                }}>
                                                                    {isRetro ? '> no scenarios found' : 'No scenarios yet'}
                                                                </Typography>
                                                                <Button
                                                                    size="small"
                                                                    startIcon={<AddIcon />}
                                                                    onClick={() => handleNewScenario(project.id)}
                                                                    sx={isRetro ? {
                                                                        color: '#5fd4c4',
                                                                        fontFamily: vortxTypography.mono,
                                                                        fontSize: '0.75rem',
                                                                        textTransform: 'uppercase',
                                                                        borderRadius: 0,
                                                                        border: '1px solid rgba(95, 212, 196, 0.3)',
                                                                        '&:hover': {
                                                                            bgcolor: 'rgba(95, 212, 196, 0.1)',
                                                                            borderColor: '#5fd4c4',
                                                                        }
                                                                    } : {
                                                                        color: vortxColors.gold,
                                                                        fontFamily: vortxTypography.body,
                                                                        textTransform: 'none',
                                                                        '&:hover': { bgcolor: alpha(vortxColors.gold, 0.1) }
                                                                    }}
                                                                >
                                                                    Create Scenario
                                                                </Button>
                                                            </Box>
                                                        ) : (
                                                            <UnifiedScenarioTable
                                                                scenarios={expandedProjects[project.id] || []}
                                                                formatConfig={(s) => `${formatSystemConfig(s)} • ${getTariffName(s)}`}
                                                                getHasResults={(s) => ['completed', 'optimal', 'Optimal Obtained'].includes(s.status) || !!s.reopt_results || !!s.last_run_uuid}
                                                                getStatusLabel={(s, hr) => s.status || (hr ? 'Completed' : 'Draft')}
                                                                getCreatedDate={(s) => s.created_at}
                                                                onRowClick={(s, e) => {
                                                                    if (e.ctrlKey || e.metaKey || e.button === 1) {
                                                                        window.open(`/projects/${project.id}/scenarios/${s.id}`, '_blank');
                                                                    } else {
                                                                        navigate(`/projects/${project.id}/scenarios/${s.id}`);
                                                                    }
                                                                }}
                                                                compareEnabled={true}
                                                                selectedForCompare={selectedForCompare[project.id] || new Set()}
                                                                onCompareToggle={(scenarioId) => {
                                                                    setSelectedForCompare(prev => {
                                                                        const projectSelections = new Set(prev[project.id] || []);
                                                                        if (projectSelections.has(scenarioId)) {
                                                                            projectSelections.delete(scenarioId);
                                                                        } else {
                                                                            projectSelections.add(scenarioId);
                                                                        }
                                                                        return { ...prev, [project.id]: projectSelections };
                                                                    });
                                                                }}
                                                                onCompare={() => openCompareModal(project.id)}
                                                                actions={[
                                                                    { id: 'share', icon: <ShareIcon />, label: 'Share', onClick: (s, e) => openScenarioShareDialog(project.id, s, e), show: (s) => ['completed', 'optimal', 'Optimal Obtained'].includes(s.status) || !!s.reopt_results || !!s.last_run_uuid },
                                                                    { id: 'duplicate', icon: <ContentCopyIcon />, label: 'Duplicate', onClick: (s, e) => openScenarioDuplicateDialog(project.id, s, e) },
                                                                    { id: 'rename', icon: <EditIcon />, label: 'Rename', onClick: (s, e) => openScenarioRenameDialog(project.id, s, e) },
                                                                    { id: 'delete', icon: <DeleteIcon />, label: 'Delete', onClick: (s, e) => openScenarioDeleteDialog(project.id, s, e) },
                                                                ]}
                                                                onCreateScenario={() => handleNewScenario(project.id)}
                                                            />
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </Fragment>
                                ))}
                        </ProjectTableBody>
                    </ProjectTableContainer>
                )}


                {/* Create Project Dialog */}
                <Dialog
                    open={open}
                    onClose={() => setOpen(false)}
                    slotProps={{
                        paper: {
                            sx: isRetro ? {
                                bgcolor: 'rgba(10, 18, 16, 0.98)',
                                borderRadius: 0,
                                border: '1px solid rgba(178, 140, 80, 0.5)',
                                boxShadow: '0 0 30px rgba(178, 140, 80, 0.2)',
                                width: '100%',
                                maxWidth: 450
                            } : {
                                bgcolor: bgCard,
                                borderRadius: 3,
                                border: `1px solid ${alpha(vortxColors.gold, 0.3)}`,
                                width: '100%',
                                maxWidth: 500
                            }
                        }
                    }}
                >
                    <DialogTitle sx={isRetro ? {
                        fontFamily: vortxTypography.heading,
                        color: vortxColors.gold,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        pb: 1,
                        borderBottom: '1px solid rgba(63, 106, 100, 0.3)',
                    } : {
                        fontFamily: vortxTypography.heading,
                        color: textPrimary,
                        fontSize: '1.5rem',
                        fontWeight: 600,
                        pb: 1
                    }}>
                        {isRetro ? 'Initialize Project' : 'Create New Project'}
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label={isRetro ? "Project Identifier" : "Project Name"}
                            fullWidth
                            value={newProjectName}
                            onChange={(e) => setNewProjectName(e.target.value)}
                            sx={isRetro ? {
                                '& .MuiOutlinedInput-root': {
                                    color: '#f4f1ec',
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.9rem',
                                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                                    borderRadius: 0,
                                    '& fieldset': { borderColor: 'rgba(63, 106, 100, 0.5)', borderRadius: 0 },
                                    '&:hover fieldset': { borderColor: vortxColors.gold },
                                    '&.Mui-focused fieldset': { borderColor: vortxColors.gold, boxShadow: `0 0 10px rgba(178, 140, 80, 0.2)` }
                                },
                                '& .MuiInputLabel-root': { color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.8rem' },
                                '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold }
                            } : {
                                '& .MuiOutlinedInput-root': {
                                    color: textPrimary,
                                    fontFamily: vortxTypography.body,
                                    bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#FFFFFF',
                                    '& fieldset': { borderColor: alpha(vortxColors.teal, 0.3) },
                                    '&:hover fieldset': { borderColor: vortxColors.gold },
                                    '&.Mui-focused fieldset': { borderColor: vortxColors.gold }
                                },
                                '& .MuiInputLabel-root': { color: vortxColors.charcoal, fontFamily: vortxTypography.body },
                                '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold }
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 2 }}>
                        <Button onClick={() => setOpen(false)} sx={isRetro ? { color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', borderRadius: 0 } : { color: vortxColors.gray, fontFamily: vortxTypography.body, textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreateProject} variant="contained" sx={isRetro ? {
                            bgcolor: 'transparent', color: vortxColors.gold, fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600, px: 3, borderRadius: 0, border: `1px solid ${vortxColors.gold}`, boxShadow: 'none',
                            '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.1)', boxShadow: `0 0 15px rgba(178, 140, 80, 0.3)` }
                        } : {
                            bgcolor: vortxColors.gold, color: '#FFFFFF', fontFamily: vortxTypography.body, textTransform: 'none', fontWeight: 600, px: 3,
                            '&:hover': { bgcolor: alpha(vortxColors.gold, 0.9) }
                        }}>
                            {isRetro ? 'Initialize' : 'Create Project'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Project Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} slotProps={{ paper: { sx: isRetro ? { bgcolor: 'rgba(10, 18, 16, 0.98)', borderRadius: 0, border: '1px solid rgba(231, 76, 60, 0.5)', boxShadow: '0 0 30px rgba(231, 76, 60, 0.2)', width: '100%', maxWidth: 450 } : { bgcolor: bgCard, borderRadius: 3, border: `1px solid ${alpha(vortxColors.gold, 0.3)}`, width: '100%', maxWidth: 500 } } }}>
                    <DialogTitle sx={isRetro ? { fontFamily: vortxTypography.heading, color: '#e74c3c', fontSize: '1.1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', pb: 1, borderBottom: '1px solid rgba(231, 76, 60, 0.3)' } : { fontFamily: vortxTypography.heading, color: textPrimary, fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
                        {isRetro ? 'Confirm Deletion' : 'Delete Project?'}
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <Typography sx={isRetro ? { fontFamily: vortxTypography.mono, fontSize: '0.8rem', color: '#c8c8c8' } : { fontFamily: vortxTypography.body, color: textPrimary }}>
                            {isRetro ? <>Warning: Deletion of project <strong style={{ color: '#e74c3c' }}>{projectToDelete?.name}</strong> is irreversible. All associated scenarios will be removed.</> : <>Are you sure you want to delete <strong>{projectToDelete?.name}</strong>? This will permanently remove the project and all scenarios.</>}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 2 }}>
                        <Button onClick={() => setDeleteDialogOpen(false)} sx={isRetro ? { color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', borderRadius: 0 } : { color: vortxColors.gray, fontFamily: vortxTypography.body, textTransform: 'none' }}>Cancel</Button>
                        <Button onClick={handleDeleteProject} variant="contained" sx={isRetro ? { bgcolor: 'transparent', color: '#e74c3c', fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600, px: 3, borderRadius: 0, border: '1px solid #e74c3c', boxShadow: 'none', '&:hover': { bgcolor: 'rgba(231, 76, 60, 0.1)', boxShadow: '0 0 15px rgba(231, 76, 60, 0.3)' } } : { bgcolor: '#ff6b6b', color: '#FFFFFF', fontFamily: vortxTypography.body, textTransform: 'none', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#ff5252' } }}>
                            {isRetro ? 'Confirm Delete' : 'Delete Project'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Rename Project Dialog */}
                <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} slotProps={{ paper: { sx: isRetro ? { bgcolor: 'rgba(10, 18, 16, 0.98)', borderRadius: 0, border: '1px solid rgba(178, 140, 80, 0.5)', boxShadow: '0 0 30px rgba(178, 140, 80, 0.2)', width: '100%', maxWidth: 450 } : { bgcolor: bgCard, borderRadius: 3, border: `1px solid ${alpha(vortxColors.gold, 0.3)}`, width: '100%', maxWidth: 500 } } }}>
                    <DialogTitle sx={isRetro ? { fontFamily: vortxTypography.heading, color: vortxColors.gold, fontSize: '1.1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', pb: 1, borderBottom: '1px solid rgba(63, 106, 100, 0.3)' } : { fontFamily: vortxTypography.heading, color: textPrimary, fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
                        Rename Project
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <TextField autoFocus margin="dense" label="Project Name" fullWidth value={tempProjectName} onChange={(e) => setTempProjectName(e.target.value)} sx={isRetro ? { '& .MuiOutlinedInput-root': { color: '#f4f1ec', fontFamily: vortxTypography.mono, fontSize: '0.9rem', bgcolor: 'rgba(0, 0, 0, 0.3)', borderRadius: 0, '& fieldset': { borderColor: 'rgba(63, 106, 100, 0.5)', borderRadius: 0 }, '&:hover fieldset': { borderColor: vortxColors.gold }, '&.Mui-focused fieldset': { borderColor: vortxColors.gold } }, '& .MuiInputLabel-root': { color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.8rem' }, '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold } } : { '& .MuiOutlinedInput-root': { color: textPrimary, fontFamily: vortxTypography.body, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#FFFFFF', '& fieldset': { borderColor: alpha(vortxColors.teal, 0.3) }, '&:hover fieldset': { borderColor: vortxColors.gold }, '&.Mui-focused fieldset': { borderColor: vortxColors.gold } }, '& .MuiInputLabel-root': { color: vortxColors.charcoal, fontFamily: vortxTypography.body }, '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold } }} />
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 2 }}>
                        <Button onClick={() => setRenameDialogOpen(false)} sx={isRetro ? { color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', borderRadius: 0 } : { color: vortxColors.gray, fontFamily: vortxTypography.body, textTransform: 'none' }}>Cancel</Button>
                        <Button onClick={handleRenameProject} variant="contained" sx={isRetro ? { bgcolor: 'transparent', color: vortxColors.gold, fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600, px: 3, borderRadius: 0, border: `1px solid ${vortxColors.gold}`, boxShadow: 'none', '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.1)', boxShadow: `0 0 15px rgba(178, 140, 80, 0.3)` } } : { bgcolor: vortxColors.gold, color: '#FFFFFF', fontFamily: vortxTypography.body, textTransform: 'none', fontWeight: 600, px: 3, '&:hover': { bgcolor: alpha(vortxColors.gold, 0.9) } }}>
                            Rename
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Scenario Rename Dialog */}
                <Dialog open={scenarioRenameDialogOpen} onClose={() => setScenarioRenameDialogOpen(false)} slotProps={{ paper: { sx: isRetro ? { bgcolor: 'rgba(10, 18, 16, 0.98)', borderRadius: 0, border: '1px solid rgba(178, 140, 80, 0.5)', boxShadow: '0 0 30px rgba(178, 140, 80, 0.2)', width: '100%', maxWidth: 450 } : { bgcolor: bgCard, borderRadius: 3, border: `1px solid ${alpha(vortxColors.gold, 0.3)}`, width: '100%', maxWidth: 500 } } }}>
                    <DialogTitle sx={isRetro ? { fontFamily: vortxTypography.heading, color: vortxColors.gold, fontSize: '1.1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', pb: 1, borderBottom: '1px solid rgba(63, 106, 100, 0.3)' } : { fontFamily: vortxTypography.heading, color: textPrimary, fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
                        Rename Scenario
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <TextField autoFocus margin="dense" label="Scenario Name" fullWidth value={tempScenarioName} onChange={(e) => setTempScenarioName(e.target.value)} sx={isRetro ? { '& .MuiOutlinedInput-root': { color: '#f4f1ec', fontFamily: vortxTypography.mono, fontSize: '0.9rem', bgcolor: 'rgba(0, 0, 0, 0.3)', borderRadius: 0, '& fieldset': { borderColor: 'rgba(63, 106, 100, 0.5)', borderRadius: 0 }, '&:hover fieldset': { borderColor: vortxColors.gold }, '&.Mui-focused fieldset': { borderColor: vortxColors.gold } }, '& .MuiInputLabel-root': { color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.8rem' }, '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold } } : { '& .MuiOutlinedInput-root': { color: textPrimary, fontFamily: vortxTypography.body, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#FFFFFF', '& fieldset': { borderColor: alpha(vortxColors.teal, 0.3) }, '&:hover fieldset': { borderColor: vortxColors.gold }, '&.Mui-focused fieldset': { borderColor: vortxColors.gold } }, '& .MuiInputLabel-root': { color: vortxColors.charcoal, fontFamily: vortxTypography.body }, '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold } }} />
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 2 }}>
                        <Button onClick={() => setScenarioRenameDialogOpen(false)} sx={isRetro ? { color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', borderRadius: 0 } : { color: vortxColors.gray, fontFamily: vortxTypography.body, textTransform: 'none' }}>Cancel</Button>
                        <Button onClick={handleRenameScenario} variant="contained" sx={isRetro ? { bgcolor: 'transparent', color: vortxColors.gold, fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600, px: 3, borderRadius: 0, border: `1px solid ${vortxColors.gold}`, boxShadow: 'none', '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.1)', boxShadow: `0 0 15px rgba(178, 140, 80, 0.3)` } } : { bgcolor: vortxColors.gold, color: '#FFFFFF', fontFamily: vortxTypography.body, textTransform: 'none', fontWeight: 600, px: 3, '&:hover': { bgcolor: alpha(vortxColors.gold, 0.9) } }}>
                            Rename
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Scenario Delete Dialog */}
                <Dialog open={scenarioDeleteDialogOpen} onClose={() => setScenarioDeleteDialogOpen(false)} slotProps={{ paper: { sx: isRetro ? { bgcolor: 'rgba(10, 18, 16, 0.98)', borderRadius: 0, border: '1px solid rgba(231, 76, 60, 0.5)', boxShadow: '0 0 30px rgba(231, 76, 60, 0.2)', width: '100%', maxWidth: 450 } : { bgcolor: bgCard, borderRadius: 3, border: `1px solid ${alpha(vortxColors.gold, 0.3)}`, width: '100%', maxWidth: 500 } } }}>
                    <DialogTitle sx={isRetro ? { fontFamily: vortxTypography.heading, color: '#e74c3c', fontSize: '1.1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', pb: 1, borderBottom: '1px solid rgba(231, 76, 60, 0.3)' } : { fontFamily: vortxTypography.heading, color: textPrimary, fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
                        {isRetro ? 'Confirm Deletion' : 'Delete Scenario?'}
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <Typography sx={isRetro ? { fontFamily: vortxTypography.mono, fontSize: '0.8rem', color: '#c8c8c8' } : { fontFamily: vortxTypography.body, color: textPrimary }}>
                            {isRetro ? <>Warning: Deletion of <strong style={{ color: '#e74c3c' }}>{selectedScenario?.name}</strong> is irreversible.</> : <>Are you sure you want to delete <strong>{selectedScenario?.name}</strong>? This will remove all associated results.</>}
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 2 }}>
                        <Button onClick={() => setScenarioDeleteDialogOpen(false)} sx={isRetro ? { color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', borderRadius: 0 } : { color: vortxColors.gray, fontFamily: vortxTypography.body, textTransform: 'none' }}>Cancel</Button>
                        <Button onClick={handleDeleteScenario} variant="contained" sx={isRetro ? { bgcolor: 'transparent', color: '#e74c3c', fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600, px: 3, borderRadius: 0, border: '1px solid #e74c3c', boxShadow: 'none', '&:hover': { bgcolor: 'rgba(231, 76, 60, 0.1)', boxShadow: '0 0 15px rgba(231, 76, 60, 0.3)' } } : { bgcolor: '#ff6b6b', color: '#FFFFFF', fontFamily: vortxTypography.body, textTransform: 'none', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#ff5252' } }}>
                            {isRetro ? 'Confirm Delete' : 'Delete Scenario'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Scenario Duplicate Dialog */}
                <Dialog open={scenarioDuplicateDialogOpen} onClose={() => setScenarioDuplicateDialogOpen(false)} slotProps={{ paper: { sx: isRetro ? { bgcolor: 'rgba(10, 18, 16, 0.98)', borderRadius: 0, border: '1px solid rgba(178, 140, 80, 0.5)', boxShadow: '0 0 30px rgba(178, 140, 80, 0.2)', width: '100%', maxWidth: 450 } : { bgcolor: bgCard, borderRadius: 3, border: `1px solid ${alpha(vortxColors.gold, 0.3)}`, width: '100%', maxWidth: 500 } } }}>
                    <DialogTitle sx={isRetro ? { fontFamily: vortxTypography.heading, color: vortxColors.gold, fontSize: '1.1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', pb: 1, borderBottom: '1px solid rgba(63, 106, 100, 0.3)' } : { fontFamily: vortxTypography.heading, color: textPrimary, fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
                        Duplicate Scenario
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <Typography sx={{ mb: 2, color: isRetro ? '#888' : textSecondary, fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body, fontSize: isRetro ? '0.8rem' : '0.9rem' }}>
                            Create a copy of <strong style={{ color: vortxColors.gold }}>{selectedScenario?.name}</strong>
                        </Typography>
                        <TextField autoFocus margin="dense" label="New Scenario Name" fullWidth value={duplicateScenarioName} onChange={(e) => setDuplicateScenarioName(e.target.value)} sx={isRetro ? { '& .MuiOutlinedInput-root': { color: '#f4f1ec', fontFamily: vortxTypography.mono, fontSize: '0.9rem', bgcolor: 'rgba(0, 0, 0, 0.3)', borderRadius: 0, '& fieldset': { borderColor: 'rgba(63, 106, 100, 0.5)', borderRadius: 0 }, '&:hover fieldset': { borderColor: vortxColors.gold }, '&.Mui-focused fieldset': { borderColor: vortxColors.gold } }, '& .MuiInputLabel-root': { color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.8rem' }, '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold } } : { '& .MuiOutlinedInput-root': { color: textPrimary, fontFamily: vortxTypography.body, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#FFFFFF', '& fieldset': { borderColor: alpha(vortxColors.teal, 0.3) }, '&:hover fieldset': { borderColor: vortxColors.gold }, '&.Mui-focused fieldset': { borderColor: vortxColors.gold } }, '& .MuiInputLabel-root': { color: vortxColors.charcoal, fontFamily: vortxTypography.body }, '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold } }} />
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 2 }}>
                        <Button onClick={() => setScenarioDuplicateDialogOpen(false)} sx={isRetro ? { color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', borderRadius: 0 } : { color: vortxColors.gray, fontFamily: vortxTypography.body, textTransform: 'none' }}>Cancel</Button>
                        <Button onClick={handleDuplicateScenario} variant="contained" sx={isRetro ? { bgcolor: 'transparent', color: vortxColors.gold, fontFamily: vortxTypography.mono, fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600, px: 3, borderRadius: 0, border: `1px solid ${vortxColors.gold}`, boxShadow: 'none', '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.1)', boxShadow: `0 0 15px rgba(178, 140, 80, 0.3)` } } : { bgcolor: vortxColors.gold, color: '#FFFFFF', fontFamily: vortxTypography.body, textTransform: 'none', fontWeight: 600, px: 3, '&:hover': { bgcolor: alpha(vortxColors.gold, 0.9) } }}>
                            Duplicate
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Compare Scenarios Modal */}
                {compareProjectId && (
                    <ScenarioCompareModalV2
                        open={compareModalOpen}
                        onClose={() => {
                            setCompareModalOpen(false);
                            clearCompareSelection(compareProjectId);
                            setCompareProjectId(null);
                            setCompareScenarios([]);
                        }}
                        scenarios={compareScenarios}
                        projectId={compareProjectId}
                        projectName={projects.find(p => p.id === compareProjectId)?.name || 'Project'}
                        loading={loadingCompare}
                    />
                )}

                {/* Snackbar */}
                <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
                </Snackbar>

                {/* Shared Pages Dashboard */}
                <SharedPagesDashboard
                    open={sharedPagesOpen}
                    onClose={() => setSharedPagesOpen(false)}
                />

                {/* Single-Scenario Share Dialog */}
                {shareScenario && (
                    <ShareConfigDialog
                        open={shareDialogOpen}
                        onClose={() => {
                            setShareDialogOpen(false);
                            setShareScenario(null);
                            setShareScenarioData(null);
                        }}
                        projectId={shareProjectId}
                        projectName={shareProjectName}
                        scenarioIds={[shareScenario.id]}
                        scenarioNames={[shareScenario.name]}
                        comparisonData={shareScenarioData ? { scenarios: [shareScenarioData], projectName: shareProjectName } : null}
                    />
                )}

                {/* Add Existing Project Dialog */}
                <AddExistingProjectDialog
                    open={addExistingOpen}
                    onClose={() => setAddExistingOpen(false)}
                    appName="site_dna"
                    appLabel="Site DNA"
                    onProjectAdded={() => reloadProjects()}
                />
            </Container>
        </Box>
    );
}
